/**
 * @file request-status-badge.tsx
 * @module app/modules/service-request/components/request-status-badge
 * @description Pill that shows whether a service request is pending, approved, or declined.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import {
  CircleCheck,
  CircleX,
  Clock,
  LoaderCircle,
  LucideIcon,
  Ticket,
  TriangleAlert,
  Undo2,
} from 'lucide-react';
import { ServiceRequestStatus } from '../types/service-request.types';
import { SERVICE_REQUEST_STATUS_UI } from '../utils/service-request.constants';

const ICONS: Record<ServiceRequestStatus, LucideIcon> = {
  [ServiceRequestStatus.Pending]: Clock,
  [ServiceRequestStatus.Processing]: LoaderCircle,
  [ServiceRequestStatus.Completed]: CircleCheck,
  [ServiceRequestStatus.Declined]: CircleX,
  [ServiceRequestStatus.Failed]: TriangleAlert,
  [ServiceRequestStatus.Withdrawn]: Undo2,
  [ServiceRequestStatus.SentToJira]: Ticket,
};

const RequestStatusBadge = ({ status }: { status: ServiceRequestStatus }) => {
  const { label, tone } = SERVICE_REQUEST_STATUS_UI[status] ?? {
    label: 'Unknown',
    tone: 'neutral',
  };
  const Icon = ICONS[status] ?? Clock;
  return (
    <span className={`sr-status sr-status--${tone}`}>
      <Icon size={14} aria-hidden="true" />
      {label}
    </span>
  );
};

export default RequestStatusBadge;
